import {
  Activity,
  ChartColumn,
  Building2,
  Coins,
  FolderKanban,
  Handshake,
  Link2,
  Radar,
  FileClock,
  FileUp,
  Globe,
  Import,
  MapIcon,
  Settings,
  User,
  Users,
  UsersRound,
} from "lucide-react";
import { CanAccess, useTranslate, useUserMenu } from "ra-core";
import { Link, matchPath, useLocation } from "react-router";
import { RefreshButton } from "@/components/admin/refresh-button";
import { ThemeModeToggle } from "@/components/admin/theme-mode-toggle";
import { UserMenu } from "@/components/admin/user-menu";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { useIsMobile } from "@/hooks/use-mobile";

import { OnboardingMenuItems } from "../onboarding";
import { LocaleToggle } from "../projects/LocaleSelector";
import { CurrencyToggle } from "../projects/CurrencySelector";

import { useConfigurationContext } from "../root/ConfigurationContext";
import { AlertBell } from "../projects/AlertList";
import { CreditGauge } from "../projects/CreditGauge";
import { PilotagePage } from "../projects/PilotagePage";
import { RadarHealthPage } from "../projects/RadarHealthPage";
import { SubscribersPage } from "../projects/SubscribersPage";
import { SourceImportsPage } from "../projects/SourceImportsPage";
import { useIsImportsOwner } from "../projects/useIsImportsOwner";
import { useInvitedState } from "../projects/useInvited";
import { canHostPartners } from "../projects/partnerships";
import { PartnershipsPage } from "../projects/PartnershipsPage";
import { usePartnerOrg } from "../projects/usePartnerOrg";
import { ImportPage } from "../misc/ImportPage";

// Aubeon : rail encre à gauche. Le bleu signal ne marque que la rubrique active.
const Header = () => {
  const { darkModeLogo, title } = useConfigurationContext();
  const location = useLocation();
  const translate = useTranslate();

  let currentPath: string | boolean = "/";
  if (matchPath("/", location.pathname)) {
    currentPath = "/";
  } else if (matchPath("/projects/*", location.pathname)) {
    currentPath = "/projects";
  } else if (matchPath("/contacts/*", location.pathname)) {
    currentPath = "/contacts";
  } else if (matchPath("/companies/*", location.pathname)) {
    currentPath = "/companies";
  } else if (matchPath("/deals/*", location.pathname)) {
    currentPath = "/deals";
  } else {
    currentPath = false;
  }

  return (
    <nav
      aria-label={translate("crm.navigation.label")}
      className="rail fixed inset-y-0 left-0 z-40 flex w-20 flex-col items-center border-r border-sidebar-border bg-sidebar py-4 text-sidebar-foreground"
    >
      <Link
        to="/"
        className="mb-6 flex size-10 items-center justify-center rounded-md"
        aria-label={title}
      >
        <img className="size-8" src={darkModeLogo} alt="" />
      </Link>
      <div className="flex flex-col items-center gap-1">
        <RailItem
          Icon={Radar}
          label={translate("crm.navigation.radar")}
          to="/"
          isActive={currentPath === "/"}
        />
        <RailItem
          Icon={FolderKanban}
          label={translate("resources.projects.name", { smart_count: 2 })}
          to="/projects"
          isActive={currentPath === "/projects"}
        />
        <RailItem
          Icon={Handshake}
          label={translate("resources.deals.name", { smart_count: 2 })}
          to="/deals"
          isActive={currentPath === "/deals"}
        />
        <RailItem
          Icon={Building2}
          label={translate("resources.companies.name", { smart_count: 2 })}
          to="/companies"
          isActive={currentPath === "/companies"}
        />
        <RailItem
          Icon={Users}
          label={translate("resources.contacts.name", { smart_count: 2 })}
          to="/contacts"
          isActive={currentPath === "/contacts"}
        />
      </div>
      <div className="mt-auto flex flex-col items-center gap-2">
        <CreditGauge variant="rail" />
        <AlertBell />
        <RefreshButton />
        <ThemeModeToggle />
        <UserMenu side="right" align="end">
          <ProfileMenu />
          <LanguageMenu />
          <CurrencyMenu />
          <CreditsMenu />
          <CanAccess resource="offer" action="read">
            <OfferMenu />
          </CanAccess>
          <CanAccess resource="subscribers" action="list">
            <SubscribersMenu />
          </CanAccess>
          <OnboardingMenuItems />
          <OrgMenu />
          <MyPartnersMenu />
          <PartnershipsMenu />
          <CanAccess resource="sales" action="list">
            <UsersMenu />
            <ExportLogMenu />
          </CanAccess>
          <CanAccess resource="user_territories" action="list">
            <PartnersMenu />
          </CanAccess>
          <SourceImportsMenu />
          <PilotageMenu />
          <RadarHealthMenu />
          <CanAccess resource="configuration" action="edit">
            <SettingsMenu />
          </CanAccess>
          <ImportFromJsonMenuItem />
        </UserMenu>
      </div>
    </nav>
  );
};

const RailItem = ({
  Icon,
  label,
  to,
  isActive,
}: {
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  label: string;
  to: string;
  isActive: boolean;
}) => (
  <Link
    to={to}
    aria-current={isActive ? "page" : undefined}
    className={`flex w-[72px] flex-col items-center gap-1 rounded-md py-2 no-underline transition-colors duration-(--motion-duration) ease-(--motion-ease) focus-visible:outline-2 focus-visible:outline-sidebar-ring ${
      isActive
        ? "bg-sidebar-accent text-white"
        : "text-[#b4becb] hover:bg-sidebar-accent/60 hover:text-white"
    }`}
  >
    <Icon
      className={`size-5 ${isActive ? "text-sidebar-primary" : ""}`}
      aria-hidden
    />
    <span className="max-w-full truncate px-1 text-[11px] font-semibold leading-4">
      {label}
    </span>
  </Link>
);

const UsersMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<UsersMenu> must be used inside <UserMenu?");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/sales" className="flex items-center gap-2">
        <Users />
        {translate("resources.sales.name", { smart_count: 2 })}
      </Link>
    </DropdownMenuItem>
  );
};

// Langue du compte : une ligne du menu, pas un élément cliquable (le menu reste ouvert au changement).
/** Monnaie du compte : montants estimés, prix de l'offre et monnaie d'une demande d'abonnement. */
const CurrencyMenu = () => {
  const translate = useTranslate();
  return (
    <div className="flex items-center justify-between gap-4 px-2 py-1.5 text-sm">
      <span className="flex items-center gap-2">
        <Coins className="size-4 text-muted-foreground" aria-hidden />
        {translate("crm.currency")}
      </span>
      <CurrencyToggle />
    </div>
  );
};

const LanguageMenu = () => {
  const translate = useTranslate();
  return (
    <div className="flex items-center justify-between gap-4 px-2 py-1.5 text-sm">
      <span className="flex items-center gap-2">
        <Globe className="size-4 text-muted-foreground" aria-hidden />
        {translate("crm.language")}
      </span>
      <LocaleToggle />
    </div>
  );
};

// Ajout du radar : chacun y voit son solde, un administrateur y attribue les crédits.
const CreditsMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<CreditsMenu> must be used inside <UserMenu?");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/credit_balances" className="flex items-center gap-2">
        <Coins />
        {translate("resources.projects.credits.title")}
      </Link>
    </DropdownMenuItem>
  );
};

// Ajout du radar : offre de l'utilisateur et estimateur de couverture, pour tous.
const OfferMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<OfferMenu> must be used inside <UserMenu?");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/offer" className="flex items-center gap-2">
        <MapIcon />
        {translate("resources.projects.offer.title")}
      </Link>
    </DropdownMenuItem>
  );
};

// Ajout du radar : pilotage des abonnés, pour le seul gestionnaire, sur ordinateur.
const SubscribersMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  const isMobile = useIsMobile();
  if (!userMenuContext) {
    throw new Error("<SubscribersMenu> must be used inside <UserMenu?");
  }
  if (isMobile) return null;
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to={SubscribersPage.path} className="flex items-center gap-2">
        <Users />
        {translate("resources.projects.subscribers.menu")}
      </Link>
    </DropdownMenuItem>
  );
};

// Ajout du radar : organisation d'un partenaire (membres et pipeline), pour les membres abonnés.
const OrgMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  const { showOrg } = usePartnerOrg();
  if (!userMenuContext) {
    throw new Error("<OrgMenu> must be used inside <UserMenu?");
  }
  if (!showOrg) return null;
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/organisation" className="flex items-center gap-2">
        <UsersRound />
        {translate("resources.deals.org.title")}
      </Link>
    </DropdownMenuItem>
  );
};

// Ajout du radar : « Mes partenaires », pour qui peut accueillir des partenaires (équipe, administrateur, abonné).
const MyPartnersMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  const { sale } = usePartnerOrg();
  if (!userMenuContext) {
    throw new Error("<MyPartnersMenu> must be used inside <UserMenu?");
  }
  if (!canHostPartners(sale)) return null;
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/partenaires" className="flex items-center gap-2">
        <Handshake />
        {translate("resources.partnerships.mine.title")}
      </Link>
    </DropdownMenuItem>
  );
};

// Ajout du radar : « Partenariats », pour tout compte partenaire (invité compris).
const PartnershipsMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  const { partner } = usePartnerOrg();
  if (!userMenuContext) {
    throw new Error("<PartnershipsMenu> must be used inside <UserMenu?");
  }
  if (!partner) return null;
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to={PartnershipsPage.path} className="flex items-center gap-2">
        <Link2 />
        {translate("resources.partnerships.hosts.title")}
      </Link>
    </DropdownMenuItem>
  );
};

// Ajout du radar : partenaires et territoires, réservé aux administrateurs.
const PartnersMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<PartnersMenu> must be used inside <UserMenu?");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/user_territories" className="flex items-center gap-2">
        <Globe />
        {translate("resources.projects.partners.title")}
      </Link>
    </DropdownMenuItem>
  );
};

// Ajout du radar : journal des exports, réservé aux administrateurs.
const ExportLogMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<ExportLogMenu> must be used inside <UserMenu?");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/export_log" className="flex items-center gap-2">
        <FileClock />
        {translate("resources.projects.export_log.menu")}
      </Link>
    </DropdownMenuItem>
  );
};

// Ajout du radar : imports mensuels, réservés au responsable des imports (pas aux administrateurs).
const SourceImportsMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  const { data: owner } = useIsImportsOwner();
  if (!userMenuContext) {
    throw new Error("<SourceImportsMenu> must be used inside <UserMenu?");
  }
  if (!owner) return null;
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to={SourceImportsPage.path} className="flex items-center gap-2">
        <FileUp />
        {translate("resources.projects.imports.menu")}
      </Link>
    </DropdownMenuItem>
  );
};

// Ajout du radar : écran de pilotage commercial, fermé à l'invité (qui ne suit pas d'affaires).
const PilotageMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  const { invited, isPending } = useInvitedState();
  if (!userMenuContext) {
    throw new Error("<PilotageMenu> must be used inside <UserMenu?");
  }
  if (invited || isPending) return null;
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to={PilotagePage.path} className="flex items-center gap-2">
        <ChartColumn />
        {translate("resources.deals.pilot.menu")}
      </Link>
    </DropdownMenuItem>
  );
};

// Ajout du radar : santé du worker, réservée au responsable des imports comme les imports.
const RadarHealthMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  const { data: owner } = useIsImportsOwner();
  if (!userMenuContext) {
    throw new Error("<RadarHealthMenu> must be used inside <UserMenu?");
  }
  if (!owner) return null;
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to={RadarHealthPage.path} className="flex items-center gap-2">
        <Activity />
        {translate("resources.projects.health.menu")}
      </Link>
    </DropdownMenuItem>
  );
};

const ProfileMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<ProfileMenu> must be used inside <UserMenu?");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/profile" className="flex items-center gap-2">
        <User />
        {translate("crm.profile.title")}
      </Link>
    </DropdownMenuItem>
  );
};

const SettingsMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<SettingsMenu> must be used inside <UserMenu>");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/settings" className="flex items-center gap-2">
        <Settings />
        {translate("crm.settings.title")}
      </Link>
    </DropdownMenuItem>
  );
};

const ImportFromJsonMenuItem = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<ImportFromJsonMenuItem> must be used inside <UserMenu>");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to={ImportPage.path} className="flex items-center gap-2">
        <Import />
        {translate("crm.header.import_data")}
      </Link>
    </DropdownMenuItem>
  );
};

export default Header;
